import { Box, Button, Flex, FlexProps, Text, TextProps } from '@chakra-ui/react';
import { ReactComponent as CrownIcon } from 'assets/icons/crown_upgrade_fill.svg';
import { UserType } from 'modules/User';
import { MouseEventHandler, ReactNode, RefObject, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface UpgradeHoverProps extends Omit<FlexProps, 'onClick'> {
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  upgradeType?: Exclude<UserType, 'default' | 'admin' | 'enterprise'> | '';
  textProps?: TextProps;
}

export const UpgradeHover = ({
  children,
  onClick,
  textProps,
  upgradeType = '',
  startAdornment,
  endAdornment,
  ...otherProps
}: UpgradeHoverProps) => {
  const { t } = useTranslation('editor_color_picker', { useSuspense: false });
  const upgradeLinkRef = useRef<HTMLButtonElement | null>(null) as RefObject<HTMLButtonElement>;

  const title = useMemo(
    () => `${t('brandKit.upgradeTo')} ${upgradeType} ${endAdornment ?? t('brandKit.toUseBrandKit')}`,
    [endAdornment, upgradeType, t],
  );

  useEffect(() => {
    // Auto focused on each appear
    upgradeLinkRef.current?.focus();
  }, []);

  return (
    <Flex
      width='full'
      height='full'
      backgroundColor='white-alpha.850'
      position='absolute'
      top={0}
      left={0}
      justifyContent={'center'}
      alignItems={'center'}
      title={title}
      data-testid={'upgrade-hover'}
      gap={0}
      {...otherProps}
    >
      {children ? (
        children
      ) : (
        <Text fontSize={'xs'} fontWeight={'medium'} {...textProps}>
          <Button
            variant='link'
            fontWeight={'600'}
            size={'xs'}
            color={'upgrade.blue.500'}
            _hover={{
              color: 'upgrade.blue.700',
              textDecoration: 'underline',
            }}
            onClick={onClick}
            p={0}
            tabIndex={0}
            ref={upgradeLinkRef}
          >
            <Box as={'span'}>
              {startAdornment ?? (
                <CrownIcon
                  color='upgrade.blue.500'
                  style={{ display: 'inline-block', verticalAlign: 'text-bottom', marginRight: '0.4rem' }}
                />
              )}
              {t('brandKit.upgradeTo')} {upgradeType}
            </Box>
          </Button>
          {endAdornment ?? ` ${t('brandKit.toUseBrandKit')}`}
        </Text>
      )}
    </Flex>
  );
};
